import { NextRouter } from 'next/router';
import { Dispatch, SetStateAction, useCallback, useReducer } from 'react';
import { cloneDeep } from 'lodash';
import {
  FilterCheckbox,
  filterDefaults,
  FilterInput,
  FilterSet,
  FilterState,
  FilterSortBy,
} from '../filters';
import { paginationDefaults } from '../../types/pagination';

const actionTypes = {
  setFilters: 'SET_FILTERS',
  updateCheckbox: 'UPDATE_CHECKBOX',
  updateCheckboxBulk: 'UPDATE_CHECKBOX_BULK',
  updateSortBy: 'UPDATE_SORT_BY',
  updateInput: 'UPDATE_INPUT',
  updateOnlyOfficeAddress: 'UPDATE_ONLY_OFFICE_ADDRESS',
  clearFilters: 'CLEAR_FILTERS',
};

function resultsFilterReducer(state, action) {
  const mapping = {
    [actionTypes.updateCheckbox]: () => {
      action.paginationSetter(paginationDefaults);
      const newState = { ...state };
      const shouldAddFilter =
        state[action.item.type].filter((v) => v.key === action.item.key)
          .length === 0;
      if (shouldAddFilter) {
        newState[action.item.type] = [...state[action.item.type], action.item];
      } else {
        newState[action.item.type] = state[action.item.type].filter(
          (v) => v.key !== action.item.key
        );
      }
      return newState;
    },
    [actionTypes.updateCheckboxBulk]: () => {
      action.paginationSetter(paginationDefaults);
      const newState = cloneDeep(state);
      action.items.forEach((item) => {
        if (action.checked) {
          if (!state[item.type].includes(item)) {
            newState[item.type].push(item);
          }
        } else {
          newState[item.type] = newState[item.type].filter(
            (v) => v.key !== item.key
          );
        }
      });
      return newState;
    },
    [actionTypes.updateSortBy]: () => {
      action.paginationSetter(paginationDefaults);
      return {
        ...state,
        [action.item.type]: {
          ...action.item,
          value: action.item.value,
        },
      };
    },
    [actionTypes.updateInput]: () => {
      action.paginationSetter(paginationDefaults);
      return {
        ...state,
        [action.item.type]: {
          ...action.item,
          value: action.item.value,
        },
      };
    },
    [actionTypes.updateOnlyOfficeAddress]: () => {
      action.paginationSetter(paginationDefaults);
      return {
        ...state,
        onlyOfficeAddress: action.item,
      };
    },
    [actionTypes.clearFilters]: () => {
      action.paginationSetter(paginationDefaults);
      return {
        ...filterDefaults,
        ...Object.entries(state)
          .filter(([key]) => Array.isArray(state[key]))
          .reduce(
            (res, [key, value]: [string, FilterCheckbox[]]) => {
              return {
                ...res,
                [key]: value.filter((i) => i.immutable),
              };
            },
            { ...filterDefaults }
          ),
        radius: state.radius,
        sortBy: state.sortBy,
      };
    },
  };

  return mapping[action.type]();
}

export default function useFilters(
  initialState: FilterSet,
  router: NextRouter,
  paginationSetter: Dispatch<SetStateAction<{ page: number; pageSize: number }>>
): FilterState {
  const [filters, dispatch] = useReducer(resultsFilterReducer, initialState);
  const updateCheckbox = useCallback(
    (item: FilterCheckbox) => {
      dispatch({
        type: actionTypes.updateCheckbox,
        item,
        router,
        paginationSetter,
      });
    },
    [router, paginationSetter]
  );
  const updateCheckboxBulk = useCallback(
    (items: Array<FilterCheckbox>, checked: boolean) => {
      dispatch({
        type: actionTypes.updateCheckboxBulk,
        items,
        checked,
        router,
        paginationSetter,
      });
    },
    [router, paginationSetter]
  );
  const updateSortBy = useCallback(
    (item: FilterSortBy) => {
      dispatch({
        type: actionTypes.updateSortBy,
        item,
        router,
        paginationSetter,
      });
    },
    [router, paginationSetter]
  );
  const updateInput = useCallback(
    (item: FilterInput) =>
      dispatch({
        type: actionTypes.updateInput,
        item,
        router,
        paginationSetter,
      }),
    [router, paginationSetter]
  );
  const updateOnlyOfficeAddress = useCallback(
    (item: boolean) => {
      dispatch({
        type: actionTypes.updateOnlyOfficeAddress,
        item,
        router,
        paginationSetter,
      });
    },
    [router, paginationSetter]
  );
  const clearFilters = useCallback(
    () =>
      dispatch({ type: actionTypes.clearFilters, router, paginationSetter }),
    [router, paginationSetter]
  );
  return {
    filters,
    updateCheckbox,
    updateCheckboxBulk,
    updateSortBy,
    updateInput,
    updateOnlyOfficeAddress,
    clearFilters,
  };
}
